import { useCallback, useEffect, useRef } from "react";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import {
  MultipartUploadPartError,
  abortUpload,
  completeUpload,
  getVideoDurationSeconds,
  initiateUpload,
  uploadParts,
  type MultipartPartFailureKind,
} from "@/services/multipartUpload";
import type { MultipartUploadInitiation } from "@/types";
import {
  buildQuotaErrorMessage,
  type QuotaErrorPayload,
} from "@/utils/quotaErrorMessage";
import { formatFileSize } from "@/utils/fileUtils";
import { formatDuration } from "@/utils/dateFormat";

export type UploadStatus = "idle" | "uploading" | "success" | "error";

/**
 * Write-through channels the hook uses to keep the upload state machine's
 * parent-owned state (and therefore every consumer of it, like the upload
 * progress banner in VideoFilesList) in sync.
 */
export interface VideoUploadStateSync {
  setSelectedFile: (file: File | null) => void;
  setUploadStatus: (status: UploadStatus) => void;
  setUploadProgress: (progress: number) => void;
  setUploadSpeed: (speed: number) => void;
  setEstimatedTimeRemaining: (time: number) => void;
  setError: (error: string) => void;
}

export interface VideoUploadCallbacks {
  onUploadComplete?: () => void;
  onSessionUsageRefresh?: () => void;
  onRequestClose?: () => void;
}

const PART_FAILURE_MESSAGE_KEYS: Record<MultipartPartFailureKind, string> = {
  presign: "pages:sessions.upload.video.errorPresignedUrl",
  upload: "pages:sessions.upload.video.errorUploadPart",
  etag: "pages:sessions.upload.video.errorEtag",
};

/**
 * Owns the video upload state machine: orphaned-upload cleanup, the multipart
 * upload protocol, progress reporting and lifecycle bookkeeping
 * (beforeunload guard, upload tracking, success auto-close).
 *
 * State values themselves live in the parent (owned before this hook
 * existed); every transition is written through `sync` so existing
 * consumers keep working unchanged.
 */
export function useVideoUpload(
  sessionId: string,
  sync: VideoUploadStateSync,
  callbacks: VideoUploadCallbacks = {},
) {
  const { t } = useTranslation(["pages", "common"]);
  const syncRef = useRef(sync);
  const callbacksRef = useRef(callbacks);
  const beforeUnloadRef = useRef<((e: BeforeUnloadEvent) => string) | null>(
    null,
  );
  const autoCloseTimerRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeUploadRef = useRef<{ uploadId: string; key: string } | null>(
    null,
  );

  useEffect(() => {
    syncRef.current = sync;
    callbacksRef.current = callbacks;
  });

  // Cleanup orphaned uploads on component mount
  useEffect(() => {
    const cleanupOrphanedUpload = async () => {
      const currentUpload = localStorage.getItem("currentUpload");
      if (currentUpload) {
        try {
          const uploadData = JSON.parse(currentUpload);

          // Attempt to abort the orphaned multipart upload
          await abortUpload(uploadData.uploadId, uploadData.key);

          localStorage.removeItem("currentUpload");
        } catch (error) {
          console.error("Failed to cleanup orphaned upload:", error);
          // Remove from localStorage anyway to prevent repeated attempts
          localStorage.removeItem("currentUpload");
        }
      }
    };

    void cleanupOrphanedUpload();
  }, []);

  // Always release lifecycle resources when the owning component unmounts
  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current !== null) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
      if (beforeUnloadRef.current) {
        window.removeEventListener("beforeunload", beforeUnloadRef.current);
        beforeUnloadRef.current = null;
      }
      abortControllerRef.current?.abort();
      const active = activeUploadRef.current;
      if (active) {
        activeUploadRef.current = null;
        localStorage.removeItem("currentUpload");
        void abortUpload(active.uploadId, active.key);
      }
    };
  }, []);

  /**
   * Resets the upload state machine to idle. This is the single reset used
   * when the modal closes in an error state; it clears exactly the fields
   * the upload flow touches.
   */
  const reset = useCallback(() => {
    syncRef.current.setSelectedFile(null);
    syncRef.current.setUploadStatus("idle");
    syncRef.current.setUploadProgress(0);
    syncRef.current.setUploadSpeed(0);
    syncRef.current.setEstimatedTimeRemaining(0);
    syncRef.current.setError("");
  }, []);

  const start = useCallback(
    async (file: File) => {
      const {
        setUploadStatus,
        setUploadProgress,
        setUploadSpeed,
        setEstimatedTimeRemaining,
        setError,
      } = syncRef.current;

      setUploadStatus("uploading");
      setUploadProgress(0);
      setError("");

      // Warn user before leaving page during upload
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        const message = t("pages:sessions.upload.video.warningInProgress");
        e.preventDefault();
        e.returnValue = message;
        return message;
      };
      beforeUnloadRef.current = handleBeforeUnload;
      window.addEventListener("beforeunload", handleBeforeUnload);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const duration = await getVideoDurationSeconds(file);

        // Step 1: Initiate multipart upload
        let initData: MultipartUploadInitiation;
        try {
          initData = await initiateUpload({
            filename: file.name,
            contentType: file.type,
            fileSize: file.size,
            sessionId,
          });
        } catch (err) {
          if (
            isAxiosError<{ error?: QuotaErrorPayload }>(err) &&
            err.response?.data?.error?.type === "quota_exceeded"
          ) {
            // Translate from the stable violation kinds; the backend message
            // is hardcoded English and never shown to the user.
            throw new Error(
              buildQuotaErrorMessage(
                err.response.data.error,
                t,
                formatFileSize,
                formatDuration
              )
            );
          }
          throw new Error(t("pages:sessions.upload.video.errorInit"));
        }

        const { uploadId, key, videoId } = initData;
        activeUploadRef.current = { uploadId, key };

        // Store upload details for cleanup if interrupted
        localStorage.setItem(
          "currentUpload",
          JSON.stringify({
            videoId,
            uploadId,
            key,
            filename: file.name,
            sessionId,
            timestamp: Date.now(),
          }),
        );

        // Step 2: Upload file in parts using presigned URLs for direct S3 upload
        const { parts: uploadedParts } = await uploadParts(file, {
          uploadId,
          key,
          signal: controller.signal,
          onProgress: ({ progress, speedBytesPerSec, etaSeconds }) => {
            setUploadProgress(progress);
            setUploadSpeed(speedBytesPerSec);
            setEstimatedTimeRemaining(etaSeconds);
          },
        });

        // Step 3: Complete the multipart upload
        try {
          await completeUpload({
            uploadId,
            key,
            sessionId,
            filename: file.name,
            fileSize: file.size,
            contentType: file.type,
            duration,
            parts: uploadedParts,
          });
        } catch {
          // Abort the multipart upload on failure
          await abortUpload(uploadId, key);
          throw new Error(t("pages:sessions.upload.video.errorComplete"));
        }

        setUploadProgress(100);

        setUploadStatus("success");

        // Notify parent that upload completed
        callbacksRef.current.onUploadComplete?.();

        // Refresh session usage after upload
        callbacksRef.current.onSessionUsageRefresh?.();

        // Remove upload tracking and navigation warning
        localStorage.removeItem("currentUpload");
        window.removeEventListener("beforeunload", handleBeforeUnload);
        beforeUnloadRef.current = null;
        activeUploadRef.current = null;

        // Auto-close after success
        autoCloseTimerRef.current = window.setTimeout(() => {
          autoCloseTimerRef.current = null;
          callbacksRef.current.onRequestClose?.();
        }, 2000);
      } catch (err: unknown) {
        // The abort came from unmount; the unmount cleanup has already
        // removed the listener, the timer, the tracking entry and aborted
        // the multipart upload. Skip state updates.
        if (controller.signal.aborted) return;

        // Remove upload tracking and navigation warning on error
        localStorage.removeItem("currentUpload");
        window.removeEventListener("beforeunload", handleBeforeUnload);
        beforeUnloadRef.current = null;
        activeUploadRef.current = null;

        setUploadStatus("error");
        if (err instanceof MultipartUploadPartError) {
          setError(
            t(PART_FAILURE_MESSAGE_KEYS[err.kind], { part: err.partNumber }),
          );
          return;
        }
        setError(
          (err as Error).message ||
            t("pages:sessions.upload.video.errorUpload"),
        );
      }
    },
    [sessionId, t],
  );

  return { start, reset };
}
